import { useCallback, useEffect, useMemo, useState } from "react";

const STORAGE_PREFIX = "admincce.dashboard.layout";
const BREAKPOINTS = ["lg", "md", "sm", "xs"];
const PROPERTIES = ["x", "y", "w", "h"];

const storageKeyFor = (source) => {
  const identity = source?.uid || source?.slug || source?.id;
  return identity ? `${STORAGE_PREFIX}.${identity}` : null;
};

const readStorage = (source) => {
  const key = storageKeyFor(source);

  if (!key) return { version: 1, tabs: {} };

  try {
    const parsed = JSON.parse(localStorage.getItem(key) || "{}");

    return {
      version: 1,
      tabs: parsed?.tabs && typeof parsed.tabs === "object" ? parsed.tabs : {},
    };
  } catch {
    return { version: 1, tabs: {} };
  }
};

const writeStorage = (source, value) => {
  const key = storageKeyFor(source);

  if (!key) return;

  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // La personalización es opcional; el dashboard sigue funcionando.
  }
};

const baseItemsFrom = (tab) =>
  (tab?.items || []).map((item) => ({
    i: item.panel,
    x: item.x,
    y: item.y,
    w: item.w,
    h: item.h,
    minW: 2,
    minH: 2,
  }));

const applyOverrides = (baseItems, overrides = {}) =>
  baseItems.map((item) => ({
    ...item,
    ...(overrides[item.i] || {}),
  }));

const diffLayout = (layout, baseLayout) => {
  const baseById = baseLayout.reduce(
    (result, item) => ({ ...result, [item.i]: item }),
    {}
  );

  return layout.reduce((result, item) => {
    const base = baseById[item.i];

    if (!base) return result;

    const changed = PROPERTIES.reduce((properties, property) => {
      if (item[property] !== base[property]) {
        properties[property] = item[property];
      }

      return properties;
    }, {});

    return Object.keys(changed).length
      ? { ...result, [item.i]: changed }
      : result;
  }, {});
};

export const useDashboardLayout = ({ source, tab }) => {
  const [editing, setEditing] = useState(false);
  const [breakpoint, setBreakpoint] = useState("lg");
  const [stored, setStored] = useState(() => readStorage(source));

  useEffect(() => {
    setStored(readStorage(source));
  }, [source?.uid, source?.slug, source?.id]);

  useEffect(() => {
    setEditing(false);
  }, [tab?.id]);

  const baseLayouts = useMemo(() => {
    const base = baseItemsFrom(tab);

    return BREAKPOINTS.reduce(
      (result, name) => ({ ...result, [name]: base.map((item) => ({ ...item })) }),
      {}
    );
  }, [tab]);

  const hiddenPanels = useMemo(
    () => stored.tabs?.[tab?.id]?.hiddenPanels || [],
    [stored, tab?.id]
  );

  const layouts = useMemo(() => {
    const tabOverrides = stored.tabs?.[tab?.id] || {};
    const hidden = new Set(hiddenPanels);

    return BREAKPOINTS.reduce(
      (result, name) => ({
        ...result,
        [name]: applyOverrides(baseLayouts[name] || [], tabOverrides[name]).filter(
          (item) => !hidden.has(item.i)
        ),
      }),
      {}
    );
  }, [baseLayouts, hiddenPanels, stored, tab?.id]);

  const persistCurrentLayout = useCallback(
    (currentLayout) => {
      if (!editing || !tab?.id) return;

      const overrides = diffLayout(
        currentLayout,
        baseLayouts[breakpoint] || []
      );

      setStored((current) => {
        const nextTabs = { ...(current.tabs || {}) };
        const nextTab = { ...(nextTabs[tab.id] || {}) };
        const hidden = new Set(nextTab.hiddenPanels || []);
        const previous = nextTab[breakpoint] || {};

        hidden.forEach((panelId) => {
          if (previous[panelId]) {
            overrides[panelId] = previous[panelId];
          }
        });

        if (Object.keys(overrides).length) {
          nextTab[breakpoint] = overrides;
        } else {
          delete nextTab[breakpoint];
        }

        if (Object.keys(nextTab).length) {
          nextTabs[tab.id] = nextTab;
        } else {
          delete nextTabs[tab.id];
        }

        const next = { version: 1, tabs: nextTabs };
        writeStorage(source, next);
        return next;
      });
    },
    [baseLayouts, breakpoint, editing, source, tab?.id]
  );

  const togglePanelVisibility = useCallback((panelId) => {
    if (!tab?.id) return;

    setStored((current) => {
      const nextTabs = { ...(current.tabs || {}) };
      const nextTab = { ...(nextTabs[tab.id] || {}) };
      const hidden = new Set(nextTab.hiddenPanels || []);

      if (hidden.has(panelId)) {
        hidden.delete(panelId);
      } else {
        hidden.add(panelId);
      }

      if (hidden.size) {
        nextTab.hiddenPanels = Array.from(hidden);
      } else {
        delete nextTab.hiddenPanels;
      }

      if (Object.keys(nextTab).length) {
        nextTabs[tab.id] = nextTab;
      } else {
        delete nextTabs[tab.id];
      }

      const next = { version: 1, tabs: nextTabs };
      writeStorage(source, next);
      return next;
    });
  }, [source, tab?.id]);

  const resetCurrentBreakpoint = useCallback(() => {
    if (!tab?.id) return;

    setStored((current) => {
      const nextTabs = { ...(current.tabs || {}) };
      const nextTab = { ...(nextTabs[tab.id] || {}) };

      delete nextTab[breakpoint];

      if (Object.keys(nextTab).length) {
        nextTabs[tab.id] = nextTab;
      } else {
        delete nextTabs[tab.id];
      }

      const next = { version: 1, tabs: nextTabs };
      writeStorage(source, next);
      return next;
    });
  }, [breakpoint, source, tab?.id]);

  const hasOverrides =
    !!tab?.id &&
    !!stored.tabs?.[tab.id]?.[breakpoint] &&
    Object.keys(stored.tabs[tab.id][breakpoint]).length > 0;

  return {
    layouts,
    editing,
    breakpoint,
    hiddenPanels,
    hasOverrides,
    setEditing,
    setBreakpoint,
    persistCurrentLayout,
    togglePanelVisibility,
    resetCurrentBreakpoint,
  };
};
